import {
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Image,
  Video,
  Mic,
  FileText,
  Sticker,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import type { Message, MediaType, MessageStatus } from '@conversia/shared-types';
import { cn } from '@/lib/cn';
import { shortTime, dateTime } from '@/lib/format';
import { hashColor } from '@/lib/avatar';

/** Icono por tipo de media, para placeholders (`[Imagen]`, `[Audio]`, …). */
const MEDIA_ICONS: Record<MediaType, LucideIcon> = {
  IMAGE: Image,
  VIDEO: Video,
  AUDIO: Mic,
  DOCUMENT: FileText,
  STICKER: Sticker,
  LOCATION: MapPin,
};

/** Icono de estado de entrega para mensajes salientes (estilo WhatsApp). */
function StatusIcon({ status }: { status: MessageStatus }) {
  switch (status) {
    case 'PENDING':
      return <Clock className="h-3 w-3 opacity-70" aria-label="Pendiente" />;
    case 'SENT':
      return <Check className="h-3 w-3 opacity-70" aria-label="Enviado" />;
    case 'DELIVERED':
      return <CheckCheck className="h-3 w-3 opacity-70" aria-label="Entregado" />;
    case 'READ':
      return <CheckCheck className="h-3 w-3 text-sky-300" aria-label="Leído" />;
    case 'FAILED':
      return <AlertCircle className="h-3 w-3 text-red-300" aria-label="Falló el envío" />;
  }
}

/**
 * Identidad legible del remitente en grupos: su nombre/pushName o, si WhatsApp
 * no lo entregó, su número derivado del jid (`+549…`). Así la burbuja siempre
 * identifica quién habló, incluso sin pushName.
 */
function senderIdentity(message: Message): string | undefined {
  if (message.senderName) return message.senderName;
  if (message.senderPushName) return message.senderPushName;
  const digits = message.senderJid?.split('@')[0]?.replace(/\D/g, '');
  return digits ? `+${digits}` : undefined;
}

/**
 * Burbuja de mensaje. Cliente a la izquierda (gris); agente AI a la derecha
 * (marca); operador humano a la derecha (violeta); sistema centrado.
 *
 * En grupos (`showSender`), las burbujas entrantes muestran el nombre del
 * participante remitente, coloreado de forma estable por su identidad.
 */
export function MessageBubble({
  message,
  showSender = false,
}: {
  message: Message;
  showSender?: boolean;
}) {
  if (message.role === 'SYSTEM') {
    return (
      <div className="my-2 flex justify-center">
        <span className="rounded-full px-3 py-1 text-xs text-slate-500 dark:text-slate-400">
          {message.content}
        </span>
      </div>
    );
  }

  const isClient = message.role === 'CLIENT';
  const isHuman = message.role === 'HUMAN' || message.role === 'HUMAN_MOBILE';
  const senderLabel = senderIdentity(message);
  const MediaIcon = message.mediaType ? MEDIA_ICONS[message.mediaType] : null;

  return (
    <div className={cn('flex', isClient ? 'justify-start' : 'justify-end')}>
      <div
        title={dateTime(message.createdAt)}
        className={cn(
          'min-w-0 max-w-[75%] break-words rounded-[18px] px-3 py-1.5 text-sm',
          isClient && 'rounded-bl-[6px] bg-[var(--mac-burbuja)] text-slate-900 dark:text-slate-100',
          !isClient && !isHuman && 'rounded-br-[6px] bg-acento-lleno text-acento-sobre',
          isHuman && 'rounded-br-[6px] bg-slate-700 text-white dark:bg-slate-600',
        )}
      >
        {showSender && isClient && senderLabel && (
          <p className="mb-0.5 text-xs font-semibold" style={{ color: hashColor(message.senderJid ?? senderLabel) }}>
            {senderLabel}
          </p>
        )}
        {message.mediaUrl && message.mediaType === 'IMAGE' ? (
          // Imagen inline; el wrapper <a> permite abrirla a tamaño completo.
          <a href={message.mediaUrl} target="_blank" rel="noreferrer" className="mb-1 block">
            <img
              src={message.mediaUrl}
              alt={message.content || 'Imagen'}
              loading="lazy"
              className="max-h-64 max-w-full rounded-lg"
            />
          </a>
        ) : message.mediaUrl && message.mediaType === 'AUDIO' ? (
          // Nota de voz / audio reproducible en el panel.
          <audio controls src={message.mediaUrl} className="mb-1 w-60 max-w-full" />
        ) : message.mediaUrl ? (
          // Resto de tipos (video, documento, etc.): link para abrir/descargar.
          <a
            href={message.mediaUrl}
            target="_blank"
            rel="noreferrer"
            className="mb-1 block text-xs underline opacity-80"
          >
            Adjunto
          </a>
        ) : null}
        <p className="flex items-center gap-1.5 whitespace-pre-wrap break-words">
          {MediaIcon && <MediaIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />}
          <span>{message.content}</span>
        </p>
        <span
          className={cn(
            'mt-0.5 flex items-center justify-end gap-1 text-[10px]',
            isClient ? 'text-slate-500 dark:text-slate-400' : 'text-white/75',
          )}
        >
          {shortTime(message.createdAt)}
          {!isClient && <StatusIcon status={message.status} />}
        </span>
      </div>
    </div>
  );
}
